import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Search, Lightbulb, ArrowRightLeft, Copy, X } from "lucide-react";
import { errMsg } from "./apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const selectCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059]";

export default function AdminJoueursDupliques() {
  const [dupliques, setDupliques] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [suggestions, setSuggestions] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/joueurs-dupliques`, { withCredentials: true })
      .then((r) => setDupliques(r.data))
      .catch(() => { toast.error("Erreur de chargement"); setDupliques([]); });
  }, []);
  useEffect(load, [load]);

  const voirSuggestions = async (d) => {
    try {
      const { data } = await axios.get(`${API}/admin/suggestions-placement/${d.registration_id}`, { withCredentials: true });
      setSuggestions({ ...data, registration_id: d.registration_id });
    } catch (e) {
      toast.error(errMsg(e, "Impossible de charger les suggestions"));
    }
  };

  if (!dupliques) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#C5A059]" /></div>;

  const filtres = dupliques.filter((d) => {
    const q = recherche.trim().toLowerCase();
    if (!q) return true;
    return `${d.prenom} ${d.nom} ${d.email || ""}`.toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-joueurs-dupliques" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl uppercase font-bold flex items-center gap-2">
            <Copy size={18} className="text-[#C5A059]" /> Joueurs Dupliqués
          </h2>
          <p className="text-sm text-[#71717A] mt-1">
            Joueurs présents dans plusieurs équipes. Les conflits de calendrier sont signalés.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un joueur…"
            data-testid="dupliques-recherche"
            className={`${selectCls} pl-9 w-64`}
          />
        </div>
      </div>

      <div className="text-xs text-[#71717A]" data-testid="dupliques-compteur">
        {filtres.length} joueur(s) dupliqué(s)
      </div>

      <div className="border border-[#27272A] divide-y divide-[#27272A]">
        {filtres.length === 0 && (
          <p className="px-5 py-10 text-center text-[#71717A] text-sm">Aucun joueur dupliqué pour le moment.</p>
        )}
        {filtres.map((d) => (
          <div key={d.registration_id} data-testid={`duplique-row-${d.registration_id.slice(0, 8)}`} className="px-5 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[190px]">
                <p className="font-medium">{d.prenom} {d.nom}</p>
                <p className="text-[11px] text-[#71717A]">{d.poste_principal}{d.poste_secondaire ? ` / ${d.poste_secondaire}` : ""}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#27272A] text-[#A1A1AA]">
                {d.genre === "féminin" ? "Fille" : "Garçon"}
              </span>
              <span className="text-xs text-[#C5A059]">{d.nb_equipes} équipe(s)</span>
              <span className="flex-1" />
              <button
                onClick={() => voirSuggestions(d.registration_id)}
                data-testid={`duplique-suggestions-${d.registration_id.slice(0, 8)}`}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
              >
                <Lightbulb size={11} /> Suggestions
              </button>
            </div>
            <div className="mt-3 space-y-1">
              {d.equipes.map((e, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="text-[#A1A1AA]">{e.equipe_nom}</span>
                  <span className="text-[#71717A]">({e.poste})</span>
                </div>
              ))}
            </div>
            {d.conflits_calendrier.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-red-400" data-testid={`duplique-conflit-${d.registration_id.slice(0, 8)}`}>
                <AlertTriangle size={12} />
                Conflit calendrier : {d.conflits_calendrier.join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal suggestions */}
      {suggestions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="suggestions-modal">
          <div className="bg-[#121214] border border-[#C5A059]/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">
                Suggestions de placement — {suggestions.joueur}
              </p>
              <button onClick={() => setSuggestions(null)} className="text-[#71717A] hover:text-white transition-colors" aria-label="Fermer">
                <X size={18} />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-xs text-[#71717A]">Équipes actuelles :</p>
              <p className="text-sm text-white mt-1">{suggestions.equipes_actuelles.join(", ") || "Aucune"}</p>
            </div>
            <div className="space-y-3">
              {suggestions.suggestions.map((s) => (
                <div key={s.equipe_id} className={`border p-4 ${s.recommandee ? "border-emerald-500/50 bg-emerald-500/5" : "border-[#27272A]"}`} data-testid={`suggestion-${s.equipe_id.slice(0, 8)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{s.equipe_nom}</p>
                      <p className="text-xs text-[#71717A] mt-1">
                        Postes libres : {s.slots_libres.map((sl) => sl.poste).join(", ")}
                      </p>
                      {s.conflits.length > 0 && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <AlertTriangle size={11} /> S'affronte déjà : {s.conflits.join(", ")}
                        </p>
                      )}
                    </div>
                    {s.recommandee && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-500/50 text-emerald-300">
                        Recommandée
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {s.slots_libres.map((sl) => (
                      <button
                        key={sl.slot}
                        onClick={async () => {
                          try {
                            const { data } = await axios.post(`${API}/admin/teams/${s.equipe_id}/ajouter`, {
                              registration_id: suggestions.registration_id,
                              slot: sl.slot,
                            }, { withCredentials: true });
                            toast.success(data.message);
                            setSuggestions(null);
                            load();
                          } catch (e) {
                            toast.error(errMsg(e, "Placement impossible"));
                          }
                        }}
                        data-testid={`suggestion-placer-${s.equipe_id.slice(0, 8)}-${sl.slot}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
                      >
                        <ArrowRightLeft size={11} /> Placer ({sl.poste})
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {suggestions.suggestions.length === 0 && (
                <p className="text-center text-[#71717A] text-sm py-8">Aucune équipe disponible pour ce joueur.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
